import { useMemo } from "react";
import type { CompetitorNameProps } from "../types";
import {
  nbsp,
  lineClampMap,
  topFourLeagues,
  textAlignMap,
} from "../js/helpers";
import { Rank } from "./Rank";
import cx from "components/src/utils/cx";
import { defaultBreakpointConfig } from "../Competitor";
import { makeSoccerAbbreviation } from "shared/src/utils/soccerAbbreviation";
import { normalizeSport } from "shared/src/utils/sportMap";

const forceByLeagueMap = {
  "college": "city",
  "football": "shortname",
  "soccer": "shortname",
  "rugby": "shortname",
  "rugbyunion": "shortname",
  "rugbyleague": "shortname"
}

const forceByLeagueType = Object.keys(forceByLeagueMap);

export const CompetitorName = ({
  displayName,
  teamInfo,
  force,
  rank,
  width,
  textSize,
  inverse,
  breakpoints,
  breakpointConfig,
  hasContainerQueries,
  hasIcon,
  hasScore,
  isWomens,
  lineClamp,
  variant,
  textAlign,
  scoreStacked,
  responsiveName,
  forcedReadableToken,
  sport: _sportRaw
}: CompetitorNameProps) => {
  const sport = normalizeSport(_sportRaw);
  const { name, abbreviation, shortName, city, nickName, league } = teamInfo;

  // Temp - remove when Athletics name changes
  const hasTeamInfo = (!!city && !!nickName && !!name) || name === "Athletics";
  const hasDisplayName =
    typeof displayName === "string"
      ? !!displayName
      : Array.isArray(displayName) && displayName.length
      ? true
      : false;
  const displayNameFromArray = Array.isArray(displayName)
    ? displayName.reduce((acc, name, index) => {
        let _name = name.split(" ").join(nbsp);
        if (index === 0) {
          acc = `${_name}${nbsp}/`;
          return acc;
        }
        acc = acc + ` ${_name}`;
        return acc;
      }, ``)
    : displayName;

  let _lineClamp = "";
  if (!lineClamp || !(lineClamp >= 1 && lineClamp <= 4)) _lineClamp = "";
  if (typeof lineClamp === "number" && lineClamp in lineClampMap)
    _lineClamp = lineClampMap[lineClamp];

  const _textSize = displayName && !Array.isArray(displayName) && displayName.length > 8 && responsiveName && !scoreStacked ? "display-md" : textSize;
  const sharedClasses = ` ${_lineClamp} ${_textSize}`;
  
  // Team Name Cascade
  let _teamNameByBreakpoint: string = shortName;
  if (!hasContainerQueries && typeof width === "number") {
    if (width && width > breakpoints.full)
      _teamNameByBreakpoint = `${city} ${nickName}`;
    if (width && width < breakpoints.abbr) _teamNameByBreakpoint = abbreviation;
    if (league === "college") _teamNameByBreakpoint = city;
    if (force) {
      switch (force) {
        case "abbr":
        case "abbreviation":
          _teamNameByBreakpoint = abbreviation;
          break;
        case "shortname":
          _teamNameByBreakpoint = shortName;
          break;
        case "fullname":
          _teamNameByBreakpoint = name;
          break;
        case "city":
          _teamNameByBreakpoint = city;
          break;
        case "nickname":
          _teamNameByBreakpoint = nickName;
      }
    }
  }

  const forceCases = {
    abbr: abbreviation,
    abbreviation: abbreviation,
    city: city,
    nickname: nickName,
    shortname: shortName,
    fullname: name,
  };

  const isForceByLeagueType = !!league && forceByLeagueType.includes(league); //league === "college" || league === "soccer";
  // const _isWomens = isWomens && !_teamNameByBreakpoint?.toLowerCase().includes("women"); 
  const womensTag = isWomens && (league === "countries" || sport === "football" || sport === "rugby") ? "Women" : isWomens ? "(W)" : null;

  let horizontalWrapperClasses = `cui-flex cui-items-center cui-gap-1 `;
  if (scoreStacked) {
    horizontalWrapperClasses +=  `${ inverse ? "cui-justify-start cui-ml-3 " : "cui-justify-end cui-mr-3"} cui-max-w-[64px]`
  }
  const _textAlign = useMemo(() => {
    return textAlign && ["left", "center", "right"].includes(textAlign)
      ? textAlignMap[textAlign]
      : "";
  }, [textAlign]);

  if (variant === "vertical") {
    const _textSize = textSize !== "heading-md" ? textSize : "body-lg";
    const sharedClasses = `cui-w-auto ${_lineClamp} cui-text-ellipsis ${_textSize} cui-text-center`;
    if (hasDisplayName) {
      return <div className={sharedClasses}>{displayName}</div>;
    }

    if (hasTeamInfo) {
      let firstPart = "";
      let secondPart = "";
      let [_abbr, ...restOfName] = teamInfo.shortName.split(" ");

      // Exception will remove
      if (name === "Athletics") {
        _abbr = "ATH";
        restOfName[0] = "Athletics";
      }
      const _forceName = force ? forceCases[force] : displayName;

      if (!force || force === "fullname" || force === "shortname") {
        if (force === "shortname" || !force) {
          let [_abbr, ...restOfName] = teamInfo.shortName.split(" ");
          firstPart = _abbr;
          secondPart = restOfName.join(" ");
        }
        if (force === "fullname") {
          firstPart = teamInfo.city;
          secondPart = teamInfo.nickName;
        }
      }

      return (
        <div className={`${sharedClasses} cui-flex cui-flex-col`}>
          {firstPart && secondPart ? (
            <>
              <span
                className={cx(
                  {
                    [forcedReadableToken as string]: !!forcedReadableToken,
                    "cui-text-fg-moderate": !!!forcedReadableToken,
                  },
                  "body-md"
                )}
              >
                {firstPart}
              </span>
              <span>
                {secondPart} {womensTag}
              </span>
            </>
          ) : (
            <span className="">
              {force ? (
                <>
                  {_forceName} {womensTag}
                </>
              ) : (
                <>
                  {teamInfo.name} {womensTag}
                </>
              )}
            </span>
          )}
        </div>
      );
    }
  }

  if (hasDisplayName) {
    const [first, ...rest] = typeof displayName === "string" && responsiveName ? displayName?.split(" ") : "";
    let _displayName = typeof displayName === "string" && responsiveName ? `${first} ${rest.join(nbsp)}` : displayName;
    _displayName = sport === "football" && responsiveName ? makeSoccerAbbreviation(displayName) : _displayName; 
    let truncateClasses = "cui-text-ellipsis";

    // Could be a unknown team w/ Placeholder
    if (responsiveName && hasIcon) truncateClasses = "cui-min-w-[66px] cui-w-auto cui-max-w-[66px] sbk-tablet:cui-max-w-[128px] cui-text-balance cui-text-ellipsis";
    // Unknown team, no icon display of any kind
    if (responsiveName && !hasIcon) truncateClasses = "cui-min-w-[120px] cui-w-auto cui-max-w-[120px] sbk-tablet:cui-max-w-[128px] cui-text-ellipsis";
    // Unknown teams w/ live scores      
    if (responsiveName && !hasIcon && scoreStacked && hasScore) truncateClasses = "cui-min-w-[66px] cui-w-auto cui-max-w-[66px] sbk-tablet:cui-max-w-[128px] cui-text-ellipsis";
    // Unknown team, no icon, no score.
    if (responsiveName && !hasIcon && !hasScore) truncateClasses = "cui-min-w-[100px] cui-w-auto cui-max-w-[100px] sbk-tablet:cui-max-w-[128px] cui-text-ellipsis";
    // Unknown competitor, no icon, no score and not live
    if (responsiveName && !hasIcon && !hasScore && !scoreStacked) truncateClasses = "cui-min-w-[100px] cui-w-auto cui-max-w-[100px] sbk-tablet:cui-max-w-[128px] cui-text-ellipsis";
     
    const _womensTag = determineWomensTag(_displayName, isWomens, womensTag);

    return (
      <div className={horizontalWrapperClasses}>
        {rank ? <Rank inverse={inverse} rank={rank} /> : null}
        <div className="cui-flex-col cui-w-full">
          <div
            className={`cui-w-auto ${truncateClasses} ${_lineClamp} ${_textSize} ${_textAlign}`.trim()}
          >
            {Array.isArray(displayName) ? displayNameFromArray : _displayName} {_womensTag}
          </div>
        </div>
      </div>
    );
  }
  if (teamInfo) {
    const _breakpointConfig = {
      ...defaultBreakpointConfig,
      ...breakpointConfig,
    };
    const _computedFullName =
      _breakpointConfig.full === "fullname" ? (
        <>
          {city} {league !== "countries" ? nickName : null} {womensTag}
        </>
      ) : (
        <>
          {_breakpointConfig.full && _breakpointConfig.full in forceCases
            ? forceCases[_breakpointConfig.full]
            : forceCases["fullname"]}
          {womensTag}
        </>
      );
    const _computedMiddle =
      _breakpointConfig.middle && _breakpointConfig.middle in forceCases ? (
        <>
          {forceCases[_breakpointConfig.middle]} {womensTag}
        </>
      ) : (
        <>
          {forceCases["shortname"]} {womensTag}
        </>
      );
    const _computedAbbr =
      _breakpointConfig.abbr && _breakpointConfig.abbr in forceCases ? (
        <>
          {forceCases[_breakpointConfig.abbr]} {womensTag}
        </>
      ) : (
        <>
          {forceCases["abbr"]} {womensTag}
        </>
      );
    return (
      <div className={horizontalWrapperClasses}>
        {rank ? <Rank inverse={inverse} rank={rank} /> : null}
        <div>
          {!hasContainerQueries && !force ? (
            <span className={`${sharedClasses} ${_textAlign}`.trim()}>
              {_teamNameByBreakpoint} {womensTag}
            </span>
          ) : null}
          {hasContainerQueries && !isForceByLeagueType && !force ? (
            <>
              <span
                className={`${sharedClasses} cui-block @[100px]:cui-hidden`}
              >
                {_computedAbbr}
              </span>
              <span
                className={`${sharedClasses} cui-hidden @[100px]:cui-block @[240px]:cui-hidden`}
              >
                {_computedMiddle}
              </span>
              <span
                className={`${sharedClasses} cui-hidden @[240px]:cui-block`}
              >
                {_computedFullName}
              </span>
            </>
          ) : null}
          {hasContainerQueries && !force && isForceByLeagueType ? (
            <span className={`${_lineClamp} ${textSize} ${_textAlign}`.trim()}>
              {/* {league === "soccer" ? shortName : city} {womensTag} */}
              { forceByLeagueMap[league] in forceCases
                ? forceCases[forceByLeagueMap[league]]
                : city} {womensTag}
            </span>
          ) : null}
          {force && force in forceCases ? (
            <span className={` ${_lineClamp} ${textSize} ${_textAlign}`.trim()}>
              {forceCases[force]} {womensTag}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
  return null;
};


// Helper fn:  Determine if "displayName" contains a women's tag or not.
function determineWomensTag(displayName, isWomens, womensTag) {
    const lowerCaseDisplayName = typeof displayName === "string" ? displayName.toLowerCase() : "";
    const hasWomenTag = lowerCaseDisplayName.includes("(w)") || lowerCaseDisplayName.includes("women");
    const _womensTag = hasWomenTag && isWomens ? "" : womensTag;
    return _womensTag;
}